import React, { useEffect, useRef, useState } from 'react';
import { Briefcase, Award, TrendingUp, Users, CheckCircle2 } from 'lucide-react';

const About = ({ data }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const highlights = [
    { icon: Briefcase, title: "15+ Years", description: "Financial Services" },
    { icon: TrendingUp, title: "WealthTech", description: "Digital Transformation" },
    { icon: Users, title: "HNI/UHNI", description: "Client Solutions" },
    { icon: Award, title: "PGDM", description: "Welingkar Institute" }
  ];

  const achievements = [
    "Rolled out 100% accurate client report across 7 asset classes and 48 products",
    "Rolled out Portfolio analysis tool and Digital account opening in a year",
    "Managed Project Transcend with IBM for Edelweiss Transformation Journey",
    "Successfully implemented 15+ small and large projects at Edelweiss",
    "Led team of 10 business intelligence professionals for PAN India MIS"
  ];

  const expertise = [
    "Process Improvement", "Business Analysis", "Operations Management",
    "Product Development", "Project Management", "Stock Reconciliation"
  ];

  return (
    <section id="about" ref={sectionRef} className="py-24 md:py-32 bg-[#0f1d32] relative overflow-hidden">
      {/* Background Accent */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#d4af37]/5 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className={`mb-16 ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="text-[#d4af37] text-sm font-semibold uppercase tracking-[0.2em]">About Me</span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4">
            Professional <span className="text-[#d4af37]">Background</span>
          </h2>
          <div className="w-20 h-1 bg-[#d4af37] mt-6" />
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Summary */}
          <div className={`space-y-8 ${isVisible ? 'animate-fade-up delay-200' : 'opacity-0'}`}>
            <p className="text-xl text-[#94a3b8] leading-relaxed">
              {data.summary}
            </p>

            {/* Expertise Tags */}
            <div>
              <h3 className="text-sm font-semibold text-[#d4af37] uppercase tracking-wider mb-4">Areas of Expertise</h3>
              <div className="flex flex-wrap gap-3">
                {expertise.map((item, index) => (
                  <span 
                    key={index}
                    className="bg-[#1a2744] text-[#94a3b8] text-sm font-medium px-4 py-2 border border-[#d4af37]/20 hover:border-[#d4af37]/50 hover:text-white transition-all duration-300"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Highlights */}
          <div className={`grid grid-cols-2 gap-4 ${isVisible ? 'animate-fade-up delay-400' : 'opacity-0'}`}>
            {highlights.map((item, index) => (
              <div
                key={index}
                className="group bg-[#1a2744] p-6 border border-[#d4af37]/10 hover:border-[#d4af37]/40 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-[#d4af37]/10 flex items-center justify-center mb-4 group-hover:bg-[#d4af37] transition-colors duration-300">
                  <item.icon size={24} className="text-[#d4af37] group-hover:text-[#0a1628] transition-colors" />
                </div>
                <h3 className="text-white font-bold text-xl mb-1">{item.title}</h3>
                <p className="text-[#64748b] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements Section */}
        <div className={`mt-16 bg-[#1a2744] p-8 border border-[#d4af37]/20 ${isVisible ? 'animate-fade-up delay-600' : 'opacity-0'}`}>
          <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
            <Award className="text-[#d4af37]" size={24} />
            Key Achievements
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {achievements.map((item, index) => (
              <div key={index} className="flex items-start gap-3 group">
                <CheckCircle2 size={20} className="text-[#d4af37] mt-0.5 flex-shrink-0" />
                <span className="text-[#94a3b8] group-hover:text-white transition-colors">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
